import React from 'react';
import { createValidationErrors, Validation } from 'ui';
import { DebounceTextarea } from 'ui/common/debounce-textarea/debounce-textarea';
import { Value } from 'app/types-shared';

import { maxDescriptionLength, maxLongDescriptionLength } from '../constants';
import { CodeDescriptionSearchData } from '../types-store';
import { CodeListItem } from './types';
import { getCodeDataId } from './get-code-data-id';
import { itemPropsAreEqual } from './item-props-are-equal';

import styles from './code-description-row.scss';

const getDescription = (value: Value) => value ? value.d : undefined;

const hideRepeatingText = (text: string | number, previousText: string | number) =>
  text !== undefined && text.toString().trim() !== '' && text !== previousText
    ? text
    : undefined;

const hideRepeatingValue = (value: Value, previousValue: Value) =>
  hideRepeatingText(
    getDescription(value),
    getDescription(previousValue)
  );

export const CodeDescriptionRow: CodeListItem = React.memo(({
  codeData = {} as CodeDescriptionSearchData, previousCodeData = {} as CodeDescriptionSearchData,
  description, longDescription,
  modifyDescription, modifyLongDescription, edited, displayLongDescription,
  descValidationMessages, longDescValidationMessages
}) => {

  const idValue = hideRepeatingText(codeData.id, previousCodeData.id);
  const codetableValue = idValue === undefined
    ? hideRepeatingValue(codeData.codetable, previousCodeData.codetable)
    : getDescription(codeData.codetable);
  const languageValue = codetableValue === undefined
    ? hideRepeatingValue(codeData.language, previousCodeData.language)
    : getDescription(codeData.language);

  const descHasErrors = (descValidationMessages?.length ?? 0) > 0;
  const descValidation = descHasErrors ? createValidationErrors(descValidationMessages) : undefined;
  const longDescHasErrors = (longDescValidationMessages?.length ?? 0) > 0;
  const longDescValidation = longDescHasErrors ? createValidationErrors(longDescValidationMessages) : undefined;

  return <tr className={descHasErrors || longDescHasErrors ? `has-danger ${styles.error}` :
    edited ? styles.edited : undefined}>
    <td>{idValue}</td>
    <td>{codetableValue}</td>
    <td>{languageValue}</td>
    <td>
      <DebounceTextarea
        className={undefined}
        id={`description_${getCodeDataId(codeData)}`}
        value={description}
        onChange={modifyDescription}
        maxLength={maxDescriptionLength}
        wait={500} />
      {descHasErrors && <Validation level={descValidation.level} messages={descValidation.messages} />}
    </td>
    <td>
      {displayLongDescription && <>
        <DebounceTextarea
          className={undefined}
          id={`longDescription_${getCodeDataId(codeData)}`}
          value={longDescription}
          onChange={modifyLongDescription}
          maxLength={maxLongDescriptionLength}
          wait={500} />
        {longDescHasErrors && <Validation level={longDescValidation.level} messages={longDescValidation.messages} />}
      </>}
    </td>
  </tr>;
}, itemPropsAreEqual);
